import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { estimateService } from '../../services/estimateService';
import { Estimate, ESTIMATE_STATUS_LABELS } from '../../types/estimate';
import { formatCurrency } from '../../utils/formatters';
import { generateEstimatePDF } from '../../utils/estimatePdfGenerator';

interface EstimateDetailProps {
  estimateId: string;
  onEdit: (estimate: Estimate) => void;
  onBack: () => void;
}

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bgBase: '#f4efe2',
  bgCard: '#ffffff',
  bgElevated: '#faf7ee',
  gold: '#d97706',
  goldLight: '#fbbf24',
  goldHover: '#b45309',
  textPrimary: '#1c2033',
  textSecondary: '#5b5a52',
  textMuted: '#8a8778',
  borderNormal: 'rgba(92,78,51,0.14)',
  borderSubtle: 'rgba(92,78,51,0.22)',
  fontHeading: "'Inter', sans-serif",
  fontLabel: "'Inter', sans-serif",
  fontBody: "'Inter', sans-serif",
};

const statusBadgeStyle = (status: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string; border: string }> = {
    DRAFT:    { bg: 'rgba(120,120,140,0.15)', color: '#a0a0b8', border: 'rgba(120,120,140,0.3)' },
    SENT:     { bg: 'rgba(90,140,201,0.15)',  color: '#7ab0e8', border: 'rgba(90,140,201,0.3)' },
    ACCEPTED: { bg: 'rgba(90,201,140,0.15)',  color: '#5ac98c', border: 'rgba(90,201,140,0.3)' },
    DECLINED: { bg: 'rgba(201,90,90,0.15)',   color: '#e87a7a', border: 'rgba(201,90,90,0.3)'  },
    EXPIRED:  { bg: 'rgba(201,140,90,0.15)',  color: '#e8b07a', border: 'rgba(201,140,90,0.3)' },
    CONVERTED:{ bg: 'rgba(140,90,201,0.15)',  color: '#c07ae8', border: 'rgba(140,90,201,0.3)' },
  };
  const s = map[status] || map.DRAFT;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 12px',
    borderRadius: 20,
    fontSize: 10,
    fontFamily: C.fontLabel,
    letterSpacing: '0.18em',
    fontWeight: 600,
    background: s.bg,
    color: s.color,
    border: `1px solid ${s.border}`,
    textTransform: 'uppercase' as const,
  };
};

const thStyle: React.CSSProperties = {
  fontFamily: C.fontLabel,
  fontSize: 9,
  letterSpacing: '0.22em',
  color: C.textMuted,
  textTransform: 'uppercase',
  padding: '10px 14px',
  background: C.bgElevated,
  borderBottom: `1px solid ${C.borderSubtle}`,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  fontFamily: C.fontBody,
  fontSize: 13,
  color: C.textSecondary,
  padding: '11px 14px',
  borderBottom: `1px solid ${C.borderNormal}`,
};

const labelStyle: React.CSSProperties = {
  fontFamily: C.fontLabel,
  fontSize: 8,
  letterSpacing: '0.2em',
  color: C.textMuted,
  textTransform: 'uppercase',
  marginBottom: 4,
};

const valueStyle: React.CSSProperties = {
  fontFamily: C.fontBody,
  fontSize: 13,
  color: C.textPrimary,
  lineHeight: 1.5,
};

export const EstimateDetail: React.FC<EstimateDetailProps> = ({ estimateId, onEdit, onBack }) => {
  const queryClient = useQueryClient();
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  const { data: estimate, isLoading, error } = useQuery({
    queryKey: ['estimate', estimateId],
    queryFn: () => estimateService.getEstimate(estimateId)
  });

  const markAsSentMutation = useMutation({
    mutationFn: estimateService.markEstimateAsSent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      alert('Estimate marked as sent successfully!');
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.message || error.message || 'Failed to mark estimate as sent'}`);
    }
  });

  const acceptMutation = useMutation({
    mutationFn: estimateService.acceptEstimate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      alert('Estimate accepted successfully!');
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.message || error.message || 'Failed to accept estimate'}`);
    }
  });

  const declineMutation = useMutation({
    mutationFn: estimateService.declineEstimate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      alert('Estimate declined successfully!');
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.message || error.message || 'Failed to decline estimate'}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: estimateService.deleteEstimate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      alert('Estimate deleted successfully!');
      onBack();
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.message || error.message || 'Failed to delete estimate'}`);
    }
  });

  const convertToInvoiceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { dueDate: string; notes?: string } }) =>
      estimateService.convertToInvoice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      setShowConvertModal(false);
      alert('Estimate converted to invoice successfully!');
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.message || error.message || 'Failed to convert estimate'}`);
    }
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleMarkAsSent = () => {
    if (window.confirm(`Mark estimate ${estimate?.estimateNumber} as sent?`)) {
      markAsSentMutation.mutate(estimateId);
    }
  };

  const handleAccept = () => {
    if (window.confirm(`Accept estimate ${estimate?.estimateNumber}?`)) {
      acceptMutation.mutate(estimateId);
    }
  };

  const handleDecline = () => {
    if (window.confirm(`Decline estimate ${estimate?.estimateNumber}? This action cannot be undone.`)) {
      declineMutation.mutate(estimateId);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Delete estimate ${estimate?.estimateNumber}? This action cannot be undone.`)) {
      deleteMutation.mutate(estimateId);
    }
  };

  const handleConvertToInvoice = () => {
    if (!dueDate) {
      alert('Please select a due date for the invoice');
      return;
    }
    convertToInvoiceMutation.mutate({
      id: estimateId,
      data: { dueDate, notes: estimate?.notes }
    });
  };

  const handleExportPDF = () => {
    if (estimate) {
      generateEstimatePDF(estimate);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: C.bgBase, padding: '32px 16px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{
            background: C.bgCard,
            border: `1px solid ${C.borderSubtle}`,
            borderRadius: 8,
            padding: 32,
          }}>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} style={{
                height: i === 1 ? 28 : 16,
                background: C.bgElevated,
                borderRadius: 4,
                marginBottom: 16,
                width: i === 1 ? '35%' : '100%',
                opacity: 0.6,
              }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error || !estimate) {
    return (
      <div style={{ minHeight: '100vh', background: C.bgBase, padding: '32px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: C.bgCard, border: `1px solid ${C.borderSubtle}`, borderRadius: 8, padding: 40, textAlign: 'center' }}>
          <p style={{ fontFamily: C.fontBody, color: C.textMuted, marginBottom: 20 }}>Estimate not found or failed to load</p>
          <button
            onClick={onBack}
            style={{
              fontFamily: C.fontLabel,
              fontSize: 10,
              letterSpacing: '0.18em',
              color: C.bgCard,
              background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
              border: 'none',
              borderRadius: 4,
              padding: '10px 24px',
              cursor: 'pointer',
              textTransform: 'uppercase',
            }}
          >
            Back to Estimates
          </button>
        </div>
      </div>
    );
  }

  const expiryWarning = estimateService.getExpiryWarning(estimate.validUntil);

  // ── Action button helper ─────────────────────────────────────────────────────
  const actionBtn = (
    id: string,
    label: string,
    onClick: () => void,
    variant: 'gold' | 'ghost' | 'danger' | 'green' | 'purple',
    disabled?: boolean
  ) => {
    const variantStyles: Record<string, React.CSSProperties> = {
      gold:   { background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgCard, border: 'none' },
      ghost:  { background: 'transparent', color: C.textSecondary, border: `1px solid ${C.borderSubtle}` },
      danger: { background: 'transparent', color: '#e87a7a', border: '1px solid rgba(201,90,90,0.3)' },
      green:  { background: 'transparent', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.3)' },
      purple: { background: 'transparent', color: '#c07ae8', border: '1px solid rgba(140,90,201,0.3)' },
    };
    const hovered = hoveredBtn === id;
    const hoverAdjust: React.CSSProperties = hovered
      ? variant === 'gold'
        ? { background: `linear-gradient(135deg, ${C.goldHover}, ${C.gold})`, transform: 'translateY(-1px)' }
        : { background: C.bgElevated }
      : {};
    return (
      <button
        key={id}
        onClick={onClick}
        disabled={disabled}
        onMouseEnter={() => setHoveredBtn(id)}
        onMouseLeave={() => setHoveredBtn(null)}
        style={{
          fontFamily: C.fontLabel,
          fontSize: 9,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          padding: '8px 18px',
          borderRadius: 4,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.2s ease',
          whiteSpace: 'nowrap',
          ...variantStyles[variant],
          ...hoverAdjust,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bgBase, padding: '32px 16px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* ── Page Header ─────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 32, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              onClick={onBack}
              onMouseEnter={() => setHoveredBtn('back')}
              onMouseLeave={() => setHoveredBtn(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: hoveredBtn === 'back' ? C.gold : C.textMuted,
                cursor: 'pointer',
                padding: 4,
                transition: 'color 0.2s',
                lineHeight: 0,
              }}
            >
              <svg width={20} height={20} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 style={{
                fontFamily: C.fontHeading,
                fontSize: 32,
                fontWeight: 300,
                color: C.textPrimary,
                letterSpacing: '0.04em',
                margin: 0,
                lineHeight: 1.1,
              }}>
                Estimate <span style={{ color: C.gold }}>{estimate.estimateNumber}</span>
              </h1>
              <div style={{ width: 48, height: 1, background: `linear-gradient(to right, ${C.gold}, transparent)`, marginTop: 6 }} />
            </div>
            <span style={statusBadgeStyle(estimate.status)}>
              {ESTIMATE_STATUS_LABELS[estimate.status]}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {estimate.status === 'DRAFT' && (
              <>
                {actionBtn('edit', 'Edit Estimate', () => onEdit(estimate), 'gold')}
                {actionBtn('mark-sent', markAsSentMutation.isPending ? 'Sending...' : 'Mark as Sent', handleMarkAsSent, 'green', markAsSentMutation.isPending)}
                {actionBtn('delete', deleteMutation.isPending ? 'Deleting...' : 'Delete', handleDelete, 'danger', deleteMutation.isPending)}
              </>
            )}
            {estimate.status === 'SENT' && (
              <>
                {actionBtn('accept', acceptMutation.isPending ? 'Accepting...' : 'Accept', handleAccept, 'green', acceptMutation.isPending)}
                {actionBtn('decline', declineMutation.isPending ? 'Declining...' : 'Decline', handleDecline, 'danger', declineMutation.isPending)}
              </>
            )}
            {estimate.status === 'ACCEPTED' && !estimate.convertedToInvoiceId && (
              actionBtn('convert', 'Convert to Invoice', () => setShowConvertModal(true), 'purple')
            )}
            {actionBtn('pdf', 'Export PDF', handleExportPDF, 'ghost')}
          </div>
        </div>

        {/* ── Expiry Warning ───────────────────────────────────────────────────── */}
        {expiryWarning && (
          <div style={{
            marginBottom: 20,
            background: 'rgba(201,140,90,0.08)',
            border: '1px solid rgba(201,140,90,0.25)',
            borderLeft: '3px solid #e8b07a',
            borderRadius: 6,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <svg width={16} height={16} viewBox="0 0 20 20" fill="#e8b07a">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p style={{ fontFamily: C.fontBody, fontSize: 13, color: '#e8b07a', margin: 0 }}>{expiryWarning}</p>
          </div>
        )}

        {/* ── Converted to Invoice Banner ──────────────────────────────────────── */}
        {estimate.convertedToInvoiceId && estimate.convertedToInvoice && (
          <div style={{
            marginBottom: 20,
            background: 'rgba(140,90,201,0.08)',
            border: '1px solid rgba(140,90,201,0.25)',
            borderLeft: '3px solid #c07ae8',
            borderRadius: 6,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <svg width={16} height={16} viewBox="0 0 20 20" fill="#c07ae8">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p style={{ fontFamily: C.fontBody, fontSize: 13, color: '#c07ae8', margin: 0 }}>
              This estimate has been converted to Invoice #{estimate.convertedToInvoice.invoiceNumber}
            </p>
          </div>
        )}

        {/* ── Estimate Card ────────────────────────────────────────────────────── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.borderSubtle}`, borderRadius: 8, overflow: 'hidden' }}>

          {/* Company / Document Header */}
          <div style={{
            borderBottom: `2px solid ${C.gold}`,
            padding: 32,
            background: `linear-gradient(180deg, ${C.bgElevated} 0%, ${C.bgCard} 100%)`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 24 }}>
              <div>
                <h2 style={{
                  fontFamily: C.fontHeading,
                  fontSize: 36,
                  fontWeight: 300,
                  color: C.textPrimary,
                  margin: '0 0 12px 0',
                  letterSpacing: '0.03em',
                }}>
                  {estimate.template?.templateCompanyName || estimate.company?.name || 'Your Company'}
                </h2>
                {estimate.company?.address && (
                  <div style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, lineHeight: 1.8 }}>
                    <p style={{ margin: 0 }}>{estimate.company.address}</p>
                    <p style={{ margin: 0 }}>
                      {estimate.company.city && `${estimate.company.city}, `}
                      {estimate.company.state} {estimate.company.postalCode}
                    </p>
                    {estimate.company.country && <p style={{ margin: 0 }}>{estimate.company.country}</p>}
                    {estimate.company.email && <p style={{ margin: 0 }}>Email: {estimate.company.email}</p>}
                    {estimate.company.phone && <p style={{ margin: 0 }}>Tel: {estimate.company.phone}</p>}
                  </div>
                )}
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{
                  background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
                  padding: '10px 28px',
                  borderRadius: 4,
                  marginBottom: 16,
                  display: 'inline-block',
                }}>
                  <span style={{ fontFamily: C.fontLabel, fontSize: 18, fontWeight: 700, color: C.bgCard, letterSpacing: '0.15em' }}>
                    ESTIMATE
                  </span>
                </div>
                <div style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, lineHeight: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
                    <span style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.18em', color: C.textMuted, textTransform: 'uppercase' }}>Estimate #</span>
                    <span style={{ color: C.textPrimary }}>{estimate.estimateNumber}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
                    <span style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.18em', color: C.textMuted, textTransform: 'uppercase' }}>Issue Date</span>
                    <span style={{ color: C.textPrimary }}>{formatDate(estimate.issueDate)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
                    <span style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.18em', color: C.textMuted, textTransform: 'uppercase' }}>Valid Until</span>
                    <span style={{ color: C.textPrimary }}>{formatDate(estimate.validUntil)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: 32 }}>

            {/* Customer & Job Info Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: estimate.job ? '1fr 1fr' : '1fr', gap: 24, marginBottom: 32 }}>
              {/* Estimate For */}
              <div style={{ background: C.bgElevated, border: `1px solid ${C.borderSubtle}`, borderRadius: 6, padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${C.borderNormal}` }}>
                  <div style={{ width: 3, height: 14, background: C.gold, borderRadius: 2 }} />
                  <span style={{ fontFamily: C.fontLabel, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase' }}>Estimate For</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <p style={{ fontFamily: C.fontHeading, fontSize: 20, color: C.textPrimary, margin: 0, fontWeight: 400 }}>
                    {estimate.customer?.name}
                  </p>
                  {estimate.customer?.address && <p style={{ ...valueStyle, margin: 0 }}>{estimate.customer.address}</p>}
                  {(estimate.customer?.city || estimate.customer?.state || estimate.customer?.postalCode) && (
                    <p style={{ ...valueStyle, margin: 0 }}>
                      {estimate.customer.city && `${estimate.customer.city}, `}
                      {estimate.customer.state} {estimate.customer.postalCode}
                    </p>
                  )}
                  {estimate.customer?.country && <p style={{ ...valueStyle, margin: 0 }}>{estimate.customer.country}</p>}
                  {estimate.customer?.email && (
                    <p style={{ ...valueStyle, margin: 0 }}>
                      <span style={{ color: C.textMuted, fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', marginRight: 6 }}>Email</span>
                      {estimate.customer.email}
                    </p>
                  )}
                  {estimate.customer?.phone && (
                    <p style={{ ...valueStyle, margin: 0 }}>
                      <span style={{ color: C.textMuted, fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', marginRight: 6 }}>Phone</span>
                      {estimate.customer.phone}
                    </p>
                  )}
                </div>
              </div>

              {estimate.job && (
                <div style={{ background: C.bgElevated, border: `1px solid ${C.borderSubtle}`, borderRadius: 6, padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${C.borderNormal}` }}>
                    <div style={{ width: 3, height: 14, background: C.gold, borderRadius: 2 }} />
                    <span style={{ fontFamily: C.fontLabel, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase' }}>Job Details</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <div style={labelStyle}>Job Number</div>
                      <div style={valueStyle}>{estimate.job.jobNumber}</div>
                    </div>
                    <div>
                      <div style={labelStyle}>Title</div>
                      <div style={valueStyle}>{estimate.job.title}</div>
                    </div>
                    <div>
                      <div style={labelStyle}>Status</div>
                      <div style={valueStyle}>{estimate.job.status}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Items Table */}
            <div style={{ overflowX: 'auto', border: `1px solid ${C.borderSubtle}`, borderRadius: 6, marginBottom: 28 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                    <th style={{ ...thStyle, textAlign: 'center', width: 80 }}>Qty</th>
                    <th style={{ ...thStyle, textAlign: 'right', width: 110 }}>Unit Price</th>
                    <th style={{ ...thStyle, textAlign: 'center', width: 80 }}>Tax %</th>
                    <th style={{ ...thStyle, textAlign: 'right', width: 120 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {estimate.items?.map((item) => (
                    <tr key={item.id} style={{ transition: 'background 0.15s' }}>
                      <td style={{ ...tdStyle, textAlign: 'left' }}>
                        <div style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textPrimary }}>{item.description}</div>
                        {item.product?.itemType && (
                          <div style={{ fontFamily: C.fontBody, fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                            Type: {item.product.itemType}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{item.quantity}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(item.rate)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{item.taxRate ? `${item.taxRate}%` : '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: C.fontHeading, fontSize: 15, color: C.goldLight }}>
                        {formatCurrency(item.amount + (item.taxAmount || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 28 }}>
              <div style={{ width: 320, background: C.bgElevated, border: `1px solid ${C.borderSubtle}`, borderRadius: 6, overflow: 'hidden' }}>
                <div style={{
                  padding: '10px 20px',
                  borderBottom: `1px solid ${C.borderSubtle}`,
                  background: 'rgba(239,232,213,0.5)',
                }}>
                  <span style={{ fontFamily: C.fontLabel, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase' }}>
                    Estimate Summary
                  </span>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.15em', color: C.textMuted, textTransform: 'uppercase' }}>Subtotal</span>
                    <span style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textSecondary }}>{formatCurrency(estimate.subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                    <span style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.15em', color: C.textMuted, textTransform: 'uppercase' }}>Tax Amount</span>
                    <span style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textSecondary }}>{formatCurrency(estimate.taxAmount)}</span>
                  </div>
                  <div style={{ borderTop: `1px solid ${C.borderSubtle}`, paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontFamily: C.fontLabel, fontSize: 9, letterSpacing: '0.18em', color: C.textPrimary, textTransform: 'uppercase' }}>Total Amount</span>
                    <span style={{ fontFamily: C.fontHeading, fontSize: 26, fontWeight: 300, color: C.goldLight, letterSpacing: '0.02em' }}>
                      {formatCurrency(estimate.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {estimate.notes && (
              <div style={{ background: C.bgElevated, border: `1px solid ${C.borderSubtle}`, borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ padding: '10px 20px', borderBottom: `1px solid ${C.borderNormal}`, background: 'rgba(239,232,213,0.5)' }}>
                  <span style={{ fontFamily: C.fontLabel, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase' }}>Notes & Terms</span>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <p style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textSecondary, lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>
                    {estimate.notes}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Convert to Invoice Modal ─────────────────────────────────────────── */}
      {showConvertModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(7,7,15,0.85)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: C.bgCard,
            border: `1px solid ${C.borderSubtle}`,
            borderRadius: 8,
            padding: 32,
            maxWidth: 480,
            width: '100%',
            margin: '0 16px',
          }}>
            <h3 style={{ fontFamily: C.fontHeading, fontSize: 26, fontWeight: 300, color: C.textPrimary, margin: '0 0 8px 0', letterSpacing: '0.03em' }}>
              Convert to Invoice
            </h3>
            <div style={{ width: 36, height: 1, background: `linear-gradient(to right, ${C.gold}, transparent)`, marginBottom: 16 }} />
            <p style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textSecondary, marginBottom: 24, lineHeight: 1.6 }}>
              Select a due date for the invoice. All estimate details will be copied to the new invoice.
            </p>

            <div style={{ marginBottom: 24 }}>
              <label style={{ ...labelStyle, display: 'block', marginBottom: 8 }}>
                Invoice Due Date <span style={{ color: '#e87a7a' }}>*</span>
              </label>
              <input
                type="date"
                id="dueDate"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 14px',
                  background: C.bgBase,
                  border: `1px solid ${C.borderSubtle}`,
                  borderRadius: 4,
                  color: C.textPrimary,
                  fontFamily: C.fontBody,
                  fontSize: 13,
                  outline: 'none',
                  colorScheme: 'light',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => { setShowConvertModal(false); setDueDate(''); }}
                disabled={convertToInvoiceMutation.isPending}
                onMouseEnter={() => setHoveredBtn('modal-cancel')}
                onMouseLeave={() => setHoveredBtn(null)}
                style={{
                  fontFamily: C.fontLabel,
                  fontSize: 9,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  padding: '10px 20px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: hoveredBtn === 'modal-cancel' ? C.bgElevated : 'transparent',
                  color: C.textSecondary,
                  border: `1px solid ${C.borderSubtle}`,
                  opacity: convertToInvoiceMutation.isPending ? 0.5 : 1,
                  transition: 'all 0.2s',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConvertToInvoice}
                disabled={convertToInvoiceMutation.isPending || !dueDate}
                onMouseEnter={() => setHoveredBtn('modal-confirm')}
                onMouseLeave={() => setHoveredBtn(null)}
                style={{
                  fontFamily: C.fontLabel,
                  fontSize: 9,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  padding: '10px 20px',
                  borderRadius: 4,
                  cursor: (convertToInvoiceMutation.isPending || !dueDate) ? 'not-allowed' : 'pointer',
                  background: hoveredBtn === 'modal-confirm'
                    ? `linear-gradient(135deg, ${C.goldHover}, ${C.gold})`
                    : `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
                  color: C.bgCard,
                  border: 'none',
                  opacity: (convertToInvoiceMutation.isPending || !dueDate) ? 0.5 : 1,
                  transition: 'all 0.2s',
                }}
              >
                {convertToInvoiceMutation.isPending ? 'Converting...' : 'Convert to Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
